import React, { useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { View, Text } from '@constants/Themed';
import { XStack, YStack, Button as TamaguiButton, Text as TamaguiText } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { StageSchema } from '../ActiveCampaigns';
import GenerateSchemaForm from '@hooks/generateSchemaForm';

interface ModernAddStageProps {
  onAdd: (values: StageSchema) => void;
  stageNumber: number;
  theme: boolean;
}

const ModernAddStage: React.FC<ModernAddStageProps> = ({
  onAdd,
  stageNumber,
  theme
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const stageSchema = {
    title: {
      type: 'string',
      validation: { required: true },
      helpText: "The name of the stage"
    },
    description: {
      type: 'string',
      validation: { required: true },
      helpText: "A brief description of this stage"
    },
    category: {
      type: 'string',
      validation: { required: true },
      helpText: "The category of the stage"
    },
    headline: {
      type: 'string',
      helpText: "The headline for this stage (used in subject lines, etc)"
    },
    owner: {
      type: 'string',
      helpText: "The team member responsible for this stage",
      ref: "user_stores"
    },
    stage_action: {
      type: 'string',
      helpText: "Select a template to automate actions at this stage",
      ref: "template_select"
    },
    when: {
      type: 'string',
      ref: "date_time",
      validation: { required: false },
      helpText: "When to execute this stage"
    },
  };

  const handleSubmit = (values: StageSchema) => {
    onAdd(values);
    setIsExpanded(false);
  };

  if (!isExpanded) {
    return (
      <Animatable.View
        animation="fadeIn"
        delay={200}
      >
        <Pressable
          onPress={() => setIsExpanded(true)}
          style={[
            styles.addButton,
            {
              backgroundColor: theme ? "#faf5ff" : "#2e1065",
              borderColor: theme ? "#7c3aed" : "#6d28d9",
            }
          ]}
        >
          <XStack alignItems="center" gap={16}>
            <View style={[
              styles.addBadge,
              {
                backgroundColor: theme ? "#ede9fe" : "#4c1d95",
              }
            ]}>
              <AntDesign
                name="plus"
                size={24}
                color={theme ? "#7c3aed" : "#a78bfa"}
              />
            </View>

            <YStack flex={1}>
              <Text style={{
                fontSize: 18,
                fontWeight: '700',
                color: theme ? "#7c3aed" : "#a78bfa"
              }}>
                Add New Stage
              </Text>
              <Text style={{
                fontSize: 14,
                color: theme ? "#9333ea" : "#c4b5fd",
                marginTop: 4
              }}>
                Create stage #{stageNumber} in your pipeline
              </Text>
            </YStack>

            <MaterialIcons
              name="arrow-forward"
              size={24}
              color={theme ? "#7c3aed" : "#a78bfa"}
            />
          </XStack>
        </Pressable>
      </Animatable.View>
    );
  }

  return (
    <Animatable.View
      animation="fadeInDown"
      duration={400}
      style={[
        styles.expandedForm,
        {
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderColor: theme ? "#7c3aed" : "#6d28d9",
        }
      ]}
    >
      {/* Header */}
      <XStack
        style={{
          padding: 20,
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottomWidth: 1,
          borderBottomColor: theme ? "#e2e8f0" : "#334155"
        }}
      >
        <XStack alignItems="center" gap={12}>
          <View style={[
            styles.stageBadge,
            {
              backgroundColor: theme ? "#ede9fe" : "#4c1d95",
            }
          ]}>
            <Text style={{
              fontSize: 20,
              fontWeight: '800',
              color: theme ? "#7c3aed" : "#a78bfa"
            }}>
              {stageNumber}
            </Text>
          </View>

          <YStack>
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? "#1e293b" : "#f1f5f9"
            }}>
              New Stage
            </Text>
            <Text style={{
              fontSize: 14,
              color: theme ? "#64748b" : "#94a3b8"
            }}>
              Configure stage details
            </Text>
          </YStack>
        </XStack>

        <TamaguiButton
          size="$2"
          chromeless
          onPress={() => setIsExpanded(false)}
          style={{
            borderWidth: 1.5,
            borderColor: theme ? "#94a3b8" : "#64748b",
            backgroundColor: theme ? "#f8fafc" : "#0f172a"
          }}
          icon={<MaterialIcons name="close" size={18} color={theme ? "#64748b" : "#94a3b8"} />}
        />
      </XStack>

      {/* Form Content */}
      <View style={{
        padding: 20,
        backgroundColor: "transparent",
        maxHeight: 450
      }}>
        <View style={{
          backgroundColor: theme ? "#f8fafc" : "#0f172a",
          borderRadius: 12,
          padding: 16,
          marginBottom: 16
        }}>
          <XStack alignItems="center" gap={12}>
            <View style={{
              backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
              borderRadius: 8,
              padding: 8
            }}>
              <MaterialIcons
                name="info-outline"
                size={20}
                color={theme ? "#0ea5e9" : "#38bdf8"}
              />
            </View>
            <YStack flex={1}>
              <Text style={{
                fontSize: 13,
                color: theme ? "#64748b" : "#94a3b8",
                lineHeight: 18
              }}>
                Add a new stage to your campaign pipeline. You can configure automated templates and assign team members.
              </Text>
            </YStack>
          </XStack>
        </View>

        <GenerateSchemaForm
          schema={stageSchema}
          buttonLabel="Add Stage"
          submitFunction={handleSubmit}
          scrollEnabled={false}
          noBackground
        />
      </View>
    </Animatable.View>
  );
};

const styles = StyleSheet.create({
  addButton: {
    borderRadius: 16,
    borderWidth: 2,
    borderStyle: 'dashed',
    padding: 20,
    shadowColor: "#7c3aed",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    minHeight: 100,
  },
  expandedForm: {
    borderRadius: 16,
    borderWidth: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 10,
    overflow: 'hidden',
  },
  addBadge: {
    width: 56,
    height: 56,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: "#7c3aed",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 4,
  },
  stageBadge: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 3,
  }
});

export default ModernAddStage;

